/**
 * Board Configuration Types
 *
 * Type definitions for board column configurations including constraints,
 * validation rules, and helper types for managing board behavior.
 */

import { BoardStatus } from './models/BoardStatus';
import { BoardTask } from './models/BoardTask';
import { UserRole } from './enums/UserRole';
export { ConfirmableField } from './ConfirmableField';
import { ConfirmableField } from './ConfirmableField';

/**
 * Configuration for the confirm-on-exit rule
 */
export interface ConfirmOnExitConfig {
  field: ConfirmableField;
}

/**
 * Configuration for the confirm-on-entry rule.
 * When requireReflection is true, the entry dialog also prompts the user
 * to select a reflection state (understood / still thinking / I need help).
 */
export interface ConfirmOnEntryConfig {
  field: ConfirmableField;
  requireReflection?: boolean;
}

/**
 * Options for the confirm-on-exit field selector
 */
export const CONFIRMABLE_FIELD_OPTIONS: {
  value: ConfirmableField;
  label: string;
  prompt: string;
}[] = [
  {
    value: ConfirmableField.ESTIMATED_POMODOROS,
    label: 'Estimated pomodoros',
    prompt: 'How many pomodoros do you estimate for this task?',
  },
  {
    value: ConfirmableField.ACTUAL_POMODOROS,
    label: 'Actual pomodoros',
    prompt: 'How many pomodoros did this task actually take?',
  },
];

/**
 * Configuration options for a board column
 * Stored in the config JSONB field of board_statuses table
 */
export interface ColumnConfig {
  /** Maximum number of cards allowed in this column per week (null/undefined = unlimited) */
  maxCards?: number | null;

  /** Cards can only move forward to higher positions (for student boards) */
  forwardOnly?: boolean;

  /** Roles that can move cards backward even if forwardOnly is true (empty/absent = blocked for everyone) */
  backwardAllowedRoles?: UserRole[];

  /** Auto-assign cards to the board owner when moved to this column (future feature) */
  autoAssign?: boolean;

  /** Send notification when a card enters this column (future feature) */
  notifyOnEnter?: boolean;

  /** Require a field to be filled before a card can leave this column */
  confirmOnExit?: ConfirmOnExitConfig | null;

  /** Require a field to be filled when a card enters this column */
  confirmOnEntry?: ConfirmOnEntryConfig | null;

  /**
   * When true, only pomodoro counts and reflection may be edited on cards in this column.
   * Structural fields (title, description, priority, deadline, assignees) are blocked.
   * Admins bypass this restriction. Useful for "Done" or archived columns.
   * SYNC: Keep synchronized with Go boardconfig.ColumnConfig.LockEdits
   */
  lockEdits?: boolean;
}

/**
 * BoardTask enriched with per-card assignee/mentor scoping, mirroring the
 * backend's task_assignments-derived isCardMentor/isCardAssignee checks
 * (see apps/consumer/processors/actions/update-task{,-status}/processor.go).
 * Used so the UI can match server-side edit authorization instead of only
 * checking the user's blanket board role.
 */
export interface BoardTaskWithAssignments extends BoardTask {
  assignee_ids?: string[];
  mentor_ids?: string[];
}

/**
 * Board status with associated cards
 * Used for rendering columns with their cards
 */
export interface BoardStatusWithCards extends BoardStatus {
  /** Cards in this status/column */
  cards: BoardTaskWithAssignments[];
}

/**
 * Role-specific board template configuration
 */
export interface BoardTemplateConfig {
  /** Template title */
  title: string;

  /** User role this template is for */
  role: UserRole;

  /** Column definitions for this template */
  columns: ColumnDefinition[];
}

/**
 * Column definition for board templates
 */
export interface ColumnDefinition {
  /** Column name */
  name: string;

  /** Position in the board (0-indexed) */
  position: number;

  /** Optional color for the column header */
  color?: string;

  /** Column configuration (constraints and behavior) */
  config?: ColumnConfig;
}

/**
 * Check if a card can be added to a column based on its configuration.
 * Assumes column.cards is already filtered to the selected week (board query uses week dates).
 * maxCards is enforced per-week, not globally.
 */
export function canAddCardToColumn(
  column: BoardStatusWithCards,
  config?: ColumnConfig
): boolean {
  const columnConfig = config || (column.config as ColumnConfig) || {};

  // If no maxCards limit, always allow
  if (!columnConfig.maxCards) {
    return true;
  }

  // Check if column is at capacity
  return column.cards.length < columnConfig.maxCards;
}

/**
 * Check if a move from source to target position is forward
 */
export function isForwardMove(
  sourcePosition: number,
  targetPosition: number
): boolean {
  return targetPosition > sourcePosition;
}

/**
 * Get display text for column capacity
 * Returns text like "2/3 cards" or empty string if no limit
 */
export function getColumnCapacityText(
  column: BoardStatusWithCards,
  config?: ColumnConfig
): string {
  const columnConfig = config || (column.config as ColumnConfig) || {};

  if (!columnConfig.maxCards) {
    return '';
  }

  return `${column.cards.length}/${columnConfig.maxCards} cards`;
}

/**
 * Check if a column is at capacity
 */
export function isColumnAtCapacity(
  column: BoardStatusWithCards,
  config?: ColumnConfig
): boolean {
  const columnConfig = config || (column.config as ColumnConfig) || {};

  if (!columnConfig.maxCards) {
    return false;
  }

  return column.cards.length >= columnConfig.maxCards;
}

/**
 * Validate a card move from source to target column
 */
export interface MoveValidationResult {
  /** Whether the move is valid */
  valid: boolean;

  /** Error message if invalid */
  error?: string;

  /** Error code for programmatic handling */
  errorCode?:
    | 'CAPACITY_EXCEEDED'
    | 'BACKWARD_MOVE'
    | 'STUDENT_BACKWARD_MOVEMENT_BLOCKED'
    | 'IN_PROGRESS_LIMIT_EXCEEDED'
    | 'CONFIRM_ON_EXIT_REQUIRED'
    | 'CONFIRM_ON_ENTRY_REQUIRED'
    | 'UNKNOWN';

  /** Attached config when errorCode is CONFIRM_ON_EXIT_REQUIRED */
  confirmOnExit?: ConfirmOnExitConfig;

  /** Attached config when errorCode is CONFIRM_ON_ENTRY_REQUIRED */
  confirmOnEntry?: ConfirmOnEntryConfig;
}

/**
 * Validate a card move based on column configurations
 */
export function validateCardMove(
  sourceColumn: BoardStatusWithCards,
  targetColumn: BoardStatusWithCards,
  userRole: UserRole,
  card?: BoardTask
): MoveValidationResult {
  const targetConfig = (targetColumn.config as ColumnConfig) || {};
  const sourceConfig = (sourceColumn.config as ColumnConfig) || {};

  // Check capacity constraint
  if (!canAddCardToColumn(targetColumn, targetConfig)) {
    return {
      valid: false,
      error: `Target column is at capacity (${targetConfig.maxCards} cards maximum per week)`,
      errorCode: 'CAPACITY_EXCEEDED',
    };
  }

  // Check forward-only constraint with role-based exceptions.
  // Empty/absent backwardAllowedRoles means backward movement is blocked for
  // everyone, including mentors and admins — matches backend enforcement in
  // update-task-status/processor.go.
  if (sourceConfig.forwardOnly) {
    const backwardAllowedRoles = sourceConfig.backwardAllowedRoles || [];
    const isBackwardMove = !isForwardMove(
      sourceColumn.position,
      targetColumn.position
    );

    if (isBackwardMove && !backwardAllowedRoles.includes(userRole)) {
      return {
        valid: false,
        error: 'Cards can only be moved forward',
        errorCode: 'STUDENT_BACKWARD_MOVEMENT_BLOCKED',
      };
    }
  }

  // Check confirmOnExit constraint on source column — skip if field already filled
  if (sourceConfig.confirmOnExit) {
    const exitField = sourceConfig.confirmOnExit.field as keyof BoardTask;
    const exitValue = card?.[exitField];
    const alreadyFilled =
      exitValue !== null &&
      exitValue !== undefined &&
      (exitValue as number) > 0;

    if (!alreadyFilled) {
      return {
        valid: false,
        error:
          'A required field must be filled before the card can leave this column',
        errorCode: 'CONFIRM_ON_EXIT_REQUIRED',
        confirmOnExit: sourceConfig.confirmOnExit,
      };
    }
  }

  // Check confirmOnEntry constraint on target column — skip if field already filled
  if (targetConfig.confirmOnEntry) {
    const entryField = targetConfig.confirmOnEntry.field as keyof BoardTask;
    const entryValue = card?.[entryField];
    const alreadyFilled =
      entryValue !== null &&
      entryValue !== undefined &&
      (entryValue as number) > 0;

    if (!alreadyFilled) {
      return {
        valid: false,
        error:
          'A required field must be filled before the card can enter this column',
        errorCode: 'CONFIRM_ON_ENTRY_REQUIRED',
        confirmOnEntry: targetConfig.confirmOnEntry,
      };
    }
  }

  return { valid: true };
}
